import React from "react";
import { Bar } from "react-chartjs-2";

function EmptyState({ message }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 text-center text-gray-200">
      <i className="fa-solid fa-chart-simple text-lg lg:text-2xl mb-3 opacity-30" />
      <p className="text-xs text-gray-200 leading-relaxed max-w-xs">{message}</p>
    </div>
  );
}

// Extracted from GrowthLoopHome.jsx unchanged, aside from becoming a
// standalone component that receives its data via props instead of
// closing over Home's local state/constants directly.
const GLOpenDashboard = ({
  lang,
  DOMAINS,
  POLICY_BENCHMARKS,
  aggregateDomainPercent,
  darkChartOptions,
}) => {
  const benchmarkData = DOMAINS.map((d) => POLICY_BENCHMARKS[d.id] || 0);
  const currentData = DOMAINS.map((d) => aggregateDomainPercent(d));
  const hasData = currentData.some((v) => v > 0);

  const chartData = {
    labels: DOMAINS.map((d) => d.title[lang]),
    datasets: [
      {
        label: lang === "hi" ? "नीति बेंचमार्क (%)" : "Policy Benchmark (%)",
        data: benchmarkData,
        backgroundColor: "rgba(75,85,99,0.6)",
        borderRadius: 4,
      },
      {
        label: lang === "hi" ? "वर्तमान संकेत (%)" : "Current Signals (%)",
        data: currentData,
        backgroundColor: "rgba(34,211,238,0.8)",
        borderRadius: 4,
      },
    ],
  };

  return (
    <div className="flex justify-center py-2">
      <div className="w-full max-w-2xl space-y-4 text-white">
        <div className="text-center">
          <h2 className="text-base font-bold text-cyan-300 uppercase tracking-wide">
            {lang === "hi" ? "ओपन डैशबोर्ड" : "Open Dashboard"}
          </h2>
          <p className="text-xs text-gray-200 mt-0.5 max-w-xs mx-auto">
            {lang === "hi"
              ? "संदर्भ बेंचमार्क के मुकाबले समग्र विकास संकेत। कोई बच्चा-स्तर एक्सपोजर नहीं।"
              : "Aggregated signals vs reference benchmarks. No child-level exposure."}
          </p>
        </div>

        <div className="bg-gray-950 border border-gray-800 rounded-xl p-3">
          {!hasData ? (
            <EmptyState
              message={
                lang === "hi"
                  ? "किसी भी आकलन को पूरा करने के बाद यहाँ डेटा दिखेगा।"
                  : "Complete any assessment to see data appear here."
              }
            />
          ) : (
            <div style={{ height: 220 }}>
              <Bar data={chartData} options={darkChartOptions()} />
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2">
          {DOMAINS.map((d) => {
            const current = aggregateDomainPercent(d);
            const benchmark = POLICY_BENCHMARKS[d.id] || 0;
            const delta = current - benchmark;
            return (
              <div key={d.id} className="border border-gray-800 rounded-xl p-3 bg-gray-950">
                <p className="text-xs font-semibold text-cyan-300 leading-snug mb-2">
                  {d.title[lang]}
                </p>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-200">
                    {lang === "hi" ? "वर्तमान" : "Current"}:{" "}
                    <span className="text-white font-bold">{current}%</span>
                  </span>
                  <span
                    className={`font-semibold ${
                      delta > 0
                        ? "text-red-400"
                        : delta < 0
                          ? "text-green-400"
                          : "text-gray-200"
                    }`}
                  >
                    {delta > 0 ? `+${delta}` : delta}%
                  </span>
                </div>
                <div className="text-xs text-gray-300 mt-0.5">
                  {lang === "hi" ? "बेंचमार्क" : "Benchmark"}: {benchmark}%
                </div>
                <div className="mt-2 h-1 bg-gray-800 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full bg-cyan-500 transition-all"
                    style={{ width: `${Math.min(current, 100)}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-xs text-gray-300 text-center">
          {lang === "hi"
            ? "यह डेटा गुमनाम और समग्र है। कोई नैदानिक अनुमान नहीं।"
            : "Data is anonymised and aggregate. No clinical inference drawn."}
        </p>
      </div>
    </div>
  );
};

export default GLOpenDashboard;
